import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { Udalost } from '../api/JsdhNotifikaceService/generated/models/Udalost';
import type { PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import { ZpravyOdAdmina } from './ZpravyOdAdmina';
import { parsePosila } from '../utils/parsePosila';
import { formatAdresa } from '../utils/adresaUdalosti';

interface Vyjezd2026InfoPanelProps {
    aktivniPoplach: Udalost;
    notifikace: PosilovaNotifikaceLogResponse[];
}

const MAX_POSILY_VISIBLE = 6;

// (parsePosila extrahováno do src/utils/parsePosila.ts pro reuse a unit testy.)

// Auto-fit text: zmenšuje fontSize dokud se text nevejde na jeden řádek (bez wrap).
interface AutoFitTextProps {
    text: string;
    maxFontPx: number;
    minFontPx: number;
    fontWeight: number;
    color?: string;
    textTransform?: 'uppercase' | 'none';
    letterSpacing?: number;
    lineHeight?: number;
}

const AutoFitText: React.FC<AutoFitTextProps> = ({
    text, maxFontPx, minFontPx, fontWeight, color, textTransform = 'none', letterSpacing = 0, lineHeight = 1,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [fontPx, setFontPx] = useState<number>(maxFontPx);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const fit = () => {
            const containerWidth = container.clientWidth;
            if (containerWidth <= 0) return;
            // Binary search v rozsahu [minFontPx, maxFontPx] na největší velikost která se vejde.
            let lo = minFontPx;
            let hi = maxFontPx;
            let best = minFontPx;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                measure.style.fontSize = `${mid}px`;
                if (measure.scrollWidth <= containerWidth) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            setFontPx(best);
        };

        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(container);
        return () => observer.disconnect();
    }, [text, maxFontPx, minFontPx, fontWeight, textTransform, letterSpacing]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', minWidth: 0 }}>
            {/* Skrytý měřící element — má stejné vlastnosti jako finální */}
            <span
                ref={measureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'nowrap',
                    fontWeight,
                    textTransform,
                    letterSpacing: `${letterSpacing}px`,
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </span>
            <Box
                component="span"
                sx={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    fontSize: `${fontPx}px`,
                    fontWeight,
                    lineHeight,
                    color: color ?? 'inherit',
                    textTransform,
                    letterSpacing: `${letterSpacing}px`,
                    paddingTop: '0.04em',
                }}
            >
                {text}
            </Box>
        </Box>
    );
};

// Multi-line auto-fit: zmenšuje fontSize dokud text zaplní max N řádků bez ořezu.
// Použité pro popis a upresneni — když text je dlouhý, fontSize se zmenší
// (binární vyhledávání mezi minFontPx a maxFontPx). Pokud i při minFontPx text přesahuje
// N řádků, zůstane WebkitLineClamp s ellipsis.
interface AutoFitMultilineProps {
    text: string;            // plain text pro měření (musí odpovídat zobrazovanému obsahu)
    maxFontPx: number;
    minFontPx: number;
    maxLines: number;
    fontWeight: number;
    color?: string;
    lineHeight?: number;
    children?: React.ReactNode;  // volitelně bohatší rendering (např. bold prefix)
}

const AutoFitMultiline: React.FC<AutoFitMultilineProps> = ({
    text, maxFontPx, minFontPx, maxLines, fontWeight, color, lineHeight = 1.25, children,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [fontPx, setFontPx] = useState<number>(maxFontPx);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const fit = () => {
            const w = container.clientWidth;
            if (w <= 0) return;
            measure.style.width = `${w}px`;

            let lo = minFontPx;
            let hi = maxFontPx;
            let best = minFontPx;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                measure.style.fontSize = `${mid}px`;
                const maxHeight = mid * lineHeight * maxLines;
                if (measure.scrollHeight <= maxHeight + 1) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            setFontPx(best);
        };
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(container);
        return () => observer.disconnect();
    }, [text, maxFontPx, minFontPx, maxLines, fontWeight, lineHeight]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', minWidth: 0, position: 'relative' }}>
            {/* Skrytý měřící prvek */}
            <Box
                ref={measureRef}
                sx={{
                    position: 'absolute', visibility: 'hidden', top: 0, left: 0,
                    fontWeight, lineHeight, wordBreak: 'break-word', whiteSpace: 'normal',
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </Box>
            <Box
                sx={{
                    fontSize: `${fontPx}px`, fontWeight, lineHeight, color: color ?? 'inherit',
                    display: '-webkit-box', WebkitLineClamp: maxLines, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
                }}
            >
                {children ?? text}
            </Box>
        </Box>
    );
};

// (InfoRow odstraněn — detaily renderujeme inline níže s tučnou adresou, "Objekt: ..." prefixem atd.)

export const Vyjezd2026InfoPanel: React.FC<Vyjezd2026InfoPanelProps> = ({ aktivniPoplach, notifikace }) => {
    const posily = aktivniPoplach.dalsiTechnikaJednotek || [];
    const posilyVisible = posily.slice(0, MAX_POSILY_VISIBLE);
    const posilyOverflow = posily.length - posilyVisible.length;
    const adresa = formatAdresa(aktivniPoplach);

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            p: 2,
            color: 'text.primary',  // explicit kvůli AutoFitMultiline (Box defaultně nemá MUI color cascade)
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflow: 'hidden',
            bgcolor: 'background.paper',
        }}>
            {/* Hlavička — typ + podtyp s minimální vertikální mezerou */}
            <Box>
                {aktivniPoplach.pred && (
                    <AutoFitText
                        text={aktivniPoplach.pred}
                        maxFontPx={72}
                        minFontPx={24}
                        fontWeight={900}
                        color="error.main"
                        textTransform="uppercase"
                        letterSpacing={1}
                        lineHeight={0.95}
                    />
                )}
                {aktivniPoplach.udalost && (
                    <Box sx={{ mt: '-0.1em' }}>
                        <AutoFitText
                            text={aktivniPoplach.udalost}
                            maxFontPx={42}
                            minFontPx={16}
                            fontWeight={700}
                            lineHeight={1}
                        />
                    </Box>
                )}
            </Box>
            <Divider />

            {/* Detaily události — velký font pro čitelnost ze 3 m, inline labely */}
            <Box sx={{ flex: '0 0 auto' }}>
                {adresa && (
                    <Typography sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {adresa}
                    </Typography>
                )}
                {aktivniPoplach.objekt && (
                    <Typography sx={{
                        fontSize: '1.5rem',
                        fontWeight: 500,
                        lineHeight: 1.25,
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>Objekt: </Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{aktivniPoplach.objekt}</Box>
                    </Typography>
                )}
                {aktivniPoplach.popis && (
                    <Box sx={{ mt: 2 }}>
                        <AutoFitMultiline
                            text={aktivniPoplach.popis}
                            maxFontPx={27}    // 1.7rem
                            minFontPx={16}    // 1rem (zlomová mez čitelnosti)
                            maxLines={4}
                            fontWeight={600}
                            lineHeight={1.25}
                        />
                    </Box>
                )}
                {aktivniPoplach.upresneni && (
                    <Box sx={{ mt: 0.5 }}>
                        <AutoFitMultiline
                            text={`Upřesnění: ${aktivniPoplach.upresneni}`}
                            maxFontPx={22}    // 1.4rem
                            minFontPx={14}    // 0.875rem
                            maxLines={3}
                            fontWeight={500}
                            lineHeight={1.3}
                            color="text.secondary"
                        >
                            <Box component="span" sx={{ fontWeight: 700 }}>Upřesnění: </Box>
                            {aktivniPoplach.upresneni}
                        </AutoFitMultiline>
                    </Box>
                )}
            </Box>

            {/* Spodní blok (posily + zprávy) — mt:auto kotví ke spodnímu okraji panelu;
                mezera vznikne za "Upřesnění" (mezi detaily a tímto blokem), zprávy a posily těsně
                nad odpověďmi. */}
            <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
                {/* Technika dalších jednotek — 2-sloupcový grid, max 5 viditelných */}
                {posily.length > 0 && (
                    <Box>
                        <Divider sx={{ mb: 1 }} />
                        <Typography sx={{
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 0.5,
                        }}>
                            Technika dalších jednotek
                        </Typography>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            columnGap: 2,
                            rowGap: 0.25,
                        }}>
                            {posilyVisible.map((t, i) => {
                                const { jednotka, typ, volaci } = parsePosila(t);
                                return (
                                    <Typography
                                        key={i}
                                        component="div"
                                        sx={{
                                            fontSize: '0.95rem',
                                            lineHeight: 1.35,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        title={t}
                                    >
                                        <Box component="span" sx={{ color: 'text.secondary' }}>{jednotka}: </Box>
                                        <Box component="span" sx={{ fontWeight: 700 }}>{typ}</Box>
                                        {volaci && (
                                            <>
                                                {' '}
                                                <Box component="span" sx={{ color: 'success.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                    {volaci}
                                                </Box>
                                            </>
                                        )}
                                    </Typography>
                                );
                            })}
                        </Box>
                        {posilyOverflow > 0 && (
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}>
                                + {posilyOverflow} další techniky
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Zprávy od admina — zobrazí jen pokud existuje aspoň jedna zpráva */}
                {notifikace.length > 0 && (
                    <Box>
                        <ZpravyOdAdmina notifikace={notifikace} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
